<template>
  <div
    class="div-editable"
    contenteditable="true"
    v-html="innerText"
    @input="changeText"
    @focus="isChange = false"
    @blur="blurFunc"
    @paste="handlePaste"
    @keydown="handleEnter"
    :placeholder="placeholder"
    ref="inputRef"
  />
</template>

<script setup name="DivEditable">
import { defineProps, ref, watch, defineEmits, nextTick,defineExpose, computed } from 'vue'
import { isMobile } from '@/utils'
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '请输入消息内容……'
  }
})

const emit = defineEmits(['update:modelValue', 'send', 'upload'])
const selection = ref(null)
const innerText = ref(null)
const isChange = ref(true)
// 监听输入框变化
watch(
  () => props.modelValue,
  (newVal) => {
    if (isChange.value) {
      innerText.value = newVal
    }
    if (!props.modelValue) {
      nextTick(() => {
        inputRef.value.innerText = ''
      })
    }
  },
  { immediate: true }
)
const currentUrl = computed(() => {
  let protocol = window.location.protocol // 协议
  let hostname = window.location.hostname // 域名
  let port = window.location.port // 端口
  return protocol + '//' + hostname + (port ? ':' + port : '')
})

const inputRef = ref(null)
const changeText = (e) => {
  emit('update:modelValue', e.target.innerHTML)
}

// 设置聚焦
const setFocus = () => {
  inputRef.value.focus()
}

const blurFunc = () => {
  isChange.value = true
  selection.value = window.getSelection()?.getRangeAt(0);

}
// 粘贴
const handlePaste = (event) => {
  event.preventDefault()
  // 获取粘贴的内容
  let pastedText = (event.clipboardData || window?.clipboardData).getData('text')
  let files = (event.clipboardData || window.clipboardData).files
  if (files && files.length > 0) {
      emit('upload', files)
  }
  let p = getCaretPosition()
  if (p) {
    inputRef.value.innerHTML = insertCharAt(inputRef.value.innerHTML, pastedText, p)
  } else {
    inputRef.value.innerHTML += pastedText
  }
  emit('update:modelValue', inputRef.value.innerHTML)
  if (!isMobile()) {
    handleFocusLast()
  }
}
// 光标聚焦
const handleFocusLast = () => {
  // 光标聚焦最后
  if (window.getSelection) {
    // 兼容 IE11 10 9 ff safari
    let range = window.getSelection()
    range.selectAllChildren(inputRef.value)
    range.collapseToEnd()
  } else if (inputRef.value.selection) {
    // 兼容 IE10 9 8 7 6
    let range = inputRef.value.selection.createRange()
    range.moveToElementText(inputRef.value)
    range.collapse(false)
    range.select()
  }
}
// 获取光标位置
const getCaretPosition = () => {
  let selection = window?.getSelection()
  if (!selection) return null
  let range = selection.getRangeAt(0)
  let preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(inputRef.value)
  preCaretRange.setEnd(range.endContainer, range.endOffset)
  return preCaretRange.toString().length
}
// 字符串指定位置插入字符
const insertCharAt = (str, char, index) => {
  if (index < 0 || index > str.length) {
    return str
  }
  return `${str.slice(0, index)}${char}${str.slice(index)}`
}

const handleEnter = (event) => {
  const flag = isMobile()
  if (!flag) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey) {
        inputRef.value.innerHTML += '<br/>';
        handleFocusLast()
        return;
      }
      emit('send')
    }
  }
}

// 插入表情
const insertEmoji = (index) => {
  const sel = window.getSelection();
  
  // 如果没有选区，尝试获取当前光标位置
  if (!selection.value) {
    // 检查是否有活动选区
    if (sel.rangeCount > 0) {
      selection.value = sel.getRangeAt(0);
    } else {
      // 如果没有活动选区，创建一个新的选区
      const range = document.createRange();
      range.selectNodeContents(inputRef.value);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      selection.value = range;
    }
  }

  let emojiUrl = `${currentUrl.value}/file/emotion/${index}.gif`
  if (process.env.VUE_EMOJI_TYPE === 'online'){
    emojiUrl = `https://res.wx.qq.com/mpres/htmledition/images/icon/emotion/${index}.gif`
  }

  const imgSpan = document.createElement('span')
  imgSpan.className = 'span-emoji'
  imgSpan.style.display = 'inline-block'
  imgSpan.style.verticalAlign = 'middle'
  const img = document.createElement('img')
  img.src = emojiUrl
  img.alt = `emoji-${index - 1}`
  img.style.width = '20px'
  img.style.height = '20px'
  img.style.margin = '0 2px'
  img.className = 'inline-emoji'
  imgSpan.appendChild(img)

  try {
    // 保存当前选区
    const currentRange = selection.value.cloneRange();
    
    // 确保选区在输入框内
    if (!inputRef.value.contains(currentRange.commonAncestorContainer)) {
      currentRange.selectNodeContents(inputRef.value);
      currentRange.collapse(false);
    }
    
    // 插入表情
    currentRange.deleteContents();
    currentRange.insertNode(imgSpan);
    
    // 将光标移动到表情后面
    currentRange.setStartAfter(imgSpan);
    currentRange.setEndAfter(imgSpan);
    
    // 更新选区
    sel.removeAllRanges();
    sel.addRange(currentRange);
    
    // 更新selection.value以便下次插入
    selection.value = currentRange;
    
    // 确保输入框保持焦点
    inputRef.value.focus();
    
    emit('update:modelValue', inputRef.value.innerHTML)
  } catch (error) {
    console.error('Error inserting emoji:', error);
    // 如果出错，重置选区到末尾
    const range = document.createRange();
    range.selectNodeContents(inputRef.value);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    selection.value = range;
  }
}

defineExpose({
  handleFocusLast,
  setFocus,
  insertEmoji
})
</script>

<style lang="less" scoped>
.div-editable {
  width: 100%;
  overflow-y: auto;
  word-break: break-all;
  outline: none;
  user-select: text;
  white-space: pre-wrap;
  text-align: left;
  padding: 0 20px;
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 500;
  color: #444444;
  line-height: 16px;
  &[contenteditable='true'] {
    user-modify: read-write-plaintext-only;
    &:empty:before {
      content: attr(placeholder);
      display: block;
      color: #ccc;
    }
  }
  :deep(*) {
    // display: inherit;
    padding: 0;
    margin: 0;
    line-height: 21px;
  }
  :deep(.span-emoji) {
    display: inline-block;
    vertical-align: middle;
  }
  :deep(.inline-emoji) {
    width: 18px;
    height: 18px;
  }
}
</style>
